import { useEffect } from 'react';
import { useBridge } from '@webview-bridge/react';

import { NotificationResponse } from '../types/appbridge';
import { BridgeType, ApiAdapter } from '../context';

interface PushNotificationTokensPayload {
  version: string;
  granted: boolean;
  expoPushToken?: { data: string };
  nativePushToken?: {
    type: 'android' | 'ios';
    data: string;
  };
}

const getTokenValue = (condition: boolean, tokenData?: string): string => {
  return condition ? tokenData || 'undefined' : 'denied';
};

export const usePushNotification = (bridge: BridgeType, brand: 'ac' | 'cp', apiInstance?: ApiAdapter) => {
  const expoTokenKey = brand === 'ac' ? 'ac_expo_token' : 'expo_token';
  const lastNotificationResponse: Partial<NotificationResponse> = useBridge(bridge.store, (state) => state.notificationsLastNotificationResponse);

  useEffect(() => {
    if (!bridge.isWebViewBridgeAvailable) return;

    const { platform } = bridge.store.getState();
    if (platform.os !== 'android') return;

    bridge.notificationsSetNotificationChannelAsync('default', {
      name: 'default',
      importance: 7,
      vibrationPattern: [0, 250, 250, 250],
      lightColor: '#FF231F7C',
      showBadge: true,
    });
  }, []);

  const registerForPushNotifications = async (): Promise<PushNotificationTokensPayload | undefined> => {
    try {
      const { device, constants } = bridge.store.getState();
      const appVersion = constants.expoConfig?.version || 'unknown';

      if (!device.isPhysicalDevice) {
        console.warn('Push notification registration skipped: running on simulator');
        return;
      }

      const initialPermission = await bridge.notificationsGetPermissionsAsync();
      let currentPermission = initialPermission.status;

      if (initialPermission.status !== 'granted') {
        const permissions = await bridge.notificationsRequestPermissionsAsync({
          ios: { allowAlert: true, allowBadge: true, allowSound: true },
        });

        currentPermission = permissions.status;
      }

      if (currentPermission !== 'granted') {
        const pushNotificationsPayload = {
          version: appVersion,
          granted: false,
        };

        return pushNotificationsPayload;
      }

      const nativePushToken = await bridge.notificationsGetDevicePushTokenAsync();
      const expoPushToken = await bridge.notificationsGetExpoPushTokenAsync();

      const pushNotificationsPayload = {
        version: appVersion,
        granted: true,
        nativePushToken: {
          type: nativePushToken.type as 'android' | 'ios',
          data: nativePushToken.data,
        },
        expoPushToken,
      };

      return pushNotificationsPayload;
    } catch (error) {
      console.error('Push Notification Error:', error);
    }
  };

  const updateTokensOnEngage = async (payload: PushNotificationTokensPayload | undefined) => {
    if (!payload || !apiInstance) return;

    const requestBody = {
      app_version: payload.version,
      [expoTokenKey]: getTokenValue(payload.granted, payload.expoPushToken?.data),
      android_token: getTokenValue(payload.granted && payload.nativePushToken?.type === 'android', payload.nativePushToken?.data),
      ios_token: getTokenValue(payload.granted && payload.nativePushToken?.type === 'ios', payload.nativePushToken?.data),
    };

    return apiInstance.post('/v2/app/update-app-push-notification', requestBody);
  };

  const applyPushOnEngage = async (email: string, isAuthenticated: boolean) => {
    if (!bridge.isWebViewBridgeAvailable) return;

    const storedPayload = localStorage.getItem('app_pn');

    if (!isAuthenticated && storedPayload) {
      localStorage.removeItem('app_pn');
      return;
    }

    if (!email) return;

    try {
      const pushNotificationsPayload = await registerForPushNotifications();

      if (!pushNotificationsPayload) return;

      const currentPayload = JSON.stringify({ ...pushNotificationsPayload, email });

      if (storedPayload === currentPayload) return;

      await updateTokensOnEngage(pushNotificationsPayload);
      localStorage.setItem('app_pn', currentPayload);
    } catch (error) {
      console.error('[PUSH_NOTIFICATION] applyPushOnEngage error:', error);
    }
  };

  return { lastNotificationResponse, registerForPushNotifications, updateTokensOnEngage, applyPushOnEngage };
};

export type PushNotificationType = ReturnType<typeof usePushNotification>;
